import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import RoleBasedNavigation from "../../components/ui/RoleBasedNavigation";
import CustomerInfoCard from "./components/CustomerInfoCard";
import CivilWorkPhase from "./components/CivilWorkPhase";
import TechnicalWorkPhase from "./components/TechnicalWorkPhase";
import Button from "../../components/ui/Button";
import Icon from "../../components/AppIcon";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../components/ui/Toast";
import {
  civilWork,
  technicalWork,
  customers,
  staffAssignments,
  notifications as notificationsAPI,
} from "../../lib/database";

const buildCapturedLocation = (latitude, longitude) => {
  const lat = Number(latitude);
  const lng = Number(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { latitude: lat, longitude: lng };
};

/** wiring_inverter: keep inverterMake + inverterSerialPhoto (+ optional legacy flags); strip removed keys */
const normalizeWiringInverter = (raw) => {
  if (!raw || typeof raw !== "object") return {};
  const w = { ...raw };
  delete w.photo1;
  delete w.photo2;
  delete w.inverterSerialNumber;
  delete w.inverter_serial_number;
  delete w.inverterNumber;
  delete w.inverter_number;
  delete w.inverter_serial;
  if (w.inverter_make != null && w.inverterMake == null)
    w.inverterMake = w.inverter_make;
  delete w.inverter_make;
  if (w.inverter_serial_photo != null && w.inverterSerialPhoto == null)
    w.inverterSerialPhoto = w.inverter_serial_photo;
  delete w.inverter_serial_photo;
  return w;
};

const PANEL_SLOT_COUNT = 4;
const STATUS_ORDER = {
  survey_not_started: 1,
  survey_inprogress: 2,
  survey_complete: 3,
  survey_unworkable: 3,
  transport_pending: 4,
  transport_inprogress: 5,
  transport_complete: 6,
  civil_pending: 7,
  civil_inprogress: 8,
  civil_complete: 9,
  technical_pending: 10,
  technical_inprogress: 11,
  technical_complete: 12,
  commissioning_not_started: 13,
  commissioning_inprogress: 14,
  commissioning_complete: 15,
};

/** Map legacy `photo` -> serialNumberPhoto; strip serial number text fields */
const sanitizeTechnicalPanels = (panels) => {
  if (!panels || typeof panels !== "object") return {};
  return Object.fromEntries(
    Object.entries(panels).map(([id, p]) => {
      if (!p || typeof p !== "object") return [id, p];
      const { serial_number, serialNumber, photo, serialNumberPhoto, ...rest } =
        p;
      const next = { ...rest };
      const snp =
        serialNumberPhoto != null && serialNumberPhoto !== ""
          ? serialNumberPhoto
          : photo != null && photo !== ""
            ? photo
            : null;
      if (snp) next.serialNumberPhoto = snp;
      return [id, next];
    }),
  );
};

/** Always persist exactly four panel slots (1–4) for the technical workflow */
const ensureFourFixedPanels = (panels) => {
  const sanitized = sanitizeTechnicalPanels(panels || {});
  const out = {};
  for (let i = 1; i <= PANEL_SLOT_COUNT; i++) {
    const from = sanitized[i] ?? sanitized[String(i)];
    out[i] = from && typeof from === "object" ? { ...from } : {};
  }
  return out;
};

const InstallationWorkflow = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [activePhase, setActivePhase] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [customerId, setCustomerId] = useState(null);
  const [technicalStep, setTechnicalStep] = useState(1); // Step navigation for technical work

  const [customerData, setCustomerData] = useState();

  const [civilData, setCivilData] = useState({
    photos: {},
  });

  const [technicalData, setTechnicalData] = useState({
    panels: ensureFourFixedPanels({}),
    wiringInverter: {},
    earthing: {},
    installationPhotos: {},
    wiring: {},
  });

  const [phaseCompletion, setPhaseCompletion] = useState({
    civil: 0,
    technical: 0,
  });

  const [isSavingCivil, setIsSavingCivil] = useState(false);
  const [isCompletingCivil, setIsCompletingCivil] = useState(false);
  const [isSavingTechnical, setIsSavingTechnical] = useState(false);
  const [isCompletingTechnical, setIsCompletingTechnical] = useState(false);
  const [civilLastSavedAt, setCivilLastSavedAt] = useState(null);
  const [technicalLastSavedAt, setTechnicalLastSavedAt] = useState(null);
  const [isVerifyingLocation, setIsVerifyingLocation] = useState(false);
  const civilSaveTimeoutRef = useRef(null);
  const CIVIL_SAVE_DEBOUNCE_MS = 1500;
  const technicalSaveTimeoutRef = useRef(null);
  const TECHNICAL_SAVE_DEBOUNCE_MS = 1500;
  const technicalDataRef = useRef(technicalData);

  useEffect(() => {
    technicalDataRef.current = technicalData;
  }, [technicalData]);

  // Determine phase based on user role
  useEffect(() => {
    if (user?.role === "worker") {
      const phaseFromUrl = searchParams.get("phase");
      if (phaseFromUrl === "civil" || phaseFromUrl === "technical") {
        setActivePhase(phaseFromUrl);
      } else {
        setActivePhase("civil");
      }
    }
  }, [user, searchParams]);

  // Load selected customer from URL query params or localStorage
  useEffect(() => {
    const loadCustomerAndSetPhase = async () => {
      try {
        let customerIdToLoad = null;

        // First, try to get customerId from URL query params
        const urlCustomerId = searchParams.get("customerId");
        if (urlCustomerId) {
          customerIdToLoad = urlCustomerId;
        } else {
          // Fallback to localStorage (for backward compatibility)
          const stored = localStorage.getItem("selectedCustomer");
          if (stored) {
            try {
              const parsed = JSON.parse(stored);
              if (parsed?.id) {
                customerIdToLoad = parsed.id;
              }
            } catch (e) {
              console.error("Error parsing stored customer:", e);
            }
          }
        }

        if (customerIdToLoad) {
          setCustomerId(customerIdToLoad);

          // Fetch and set customer data
          try {
            const customer = await customers.getById(customerIdToLoad);
            setCustomerData(customer);

            // Update customer status when phase is first accessed
            if (customer && user?.role) {
              if (
                activePhase === "civil" &&
                (STATUS_ORDER[customer?.status] || 0) <
                  (STATUS_ORDER.civil_inprogress || 0)
              ) {
                await customers.update(customerIdToLoad, {
                  status: "civil_inprogress",
                });
              } else if (
                activePhase === "technical" &&
                (STATUS_ORDER[customer?.status] || 0) <
                  (STATUS_ORDER.technical_inprogress || 0)
              ) {
                await customers.update(customerIdToLoad, {
                  status: "technical_inprogress",
                });
              }
            }
          } catch (error) {
            console.error("Error fetching or updating customer:", error);
          }
        }
      } catch (error) {
        console.error("Error loading customer:", error);
      }
    };

    if (user) {
      loadCustomerAndSetPhase();
    }
  }, [user, searchParams, activePhase]);

  // Load existing phase data for this customer (if any) - only load data for user's role
  useEffect(() => {
    if (!customerId || !user?.role) return;

    const loadPhaseData = async () => {
      try {
        if (activePhase === "civil") {
          // Only load civil work data for civil users
          const existing = await civilWork.getByCustomerId(customerId);
          if (existing) {
            setCivilData({
              photos: existing.photos || {},
            });
          }
        } else if (activePhase === "technical") {
          // Only load technical work data for technical users
          const existingTechnical =
            await technicalWork.getByCustomerId(customerId);
          if (existingTechnical) {
            setTechnicalData({
              panels: ensureFourFixedPanels(existingTechnical.panels || {}),
              wiringInverter: normalizeWiringInverter(
                existingTechnical.wiring_inverter || {},
              ),
              earthing: existingTechnical.earthing || {},
              installationPhotos: existingTechnical.installation_photos || {},
              wiring: existingTechnical.wiring || {},
            });
          }
        }
      } catch (error) {
        console.error("Error loading installation phase data:", error);
      }
    };

    loadPhaseData();
  }, [customerId, user?.role, activePhase]);

  useEffect(() => {
    const civilProgress = 100;

    const filledPanels = [1, 2, 3, 4].filter((id) => {
      const p = technicalData?.panels?.[id];
      return Boolean(p?.serialNumberPhoto);
    }).length;
    const panelsProgress = (filledPanels / PANEL_SLOT_COUNT) * 100;

    const wiringInverter = technicalData?.wiringInverter || {};
    const invMakeText = String(
      wiringInverter?.inverterMake ?? wiringInverter?.inverter_make ?? "",
    ).trim();
    const serialPhoto =
      wiringInverter?.inverterSerialPhoto ??
      wiringInverter?.inverter_serial_photo;
    const wiringInverterProgress = invMakeText && serialPhoto ? 100 : 0;

    const earthing = technicalData?.earthing || {};
    const earthingProgress = earthing?.photo1 ? 100 : 0;

    const wiring = technicalData?.wiring || {};
    const wiringProgress =
      String(wiring?.dcRedFeet || "").trim() &&
      String(wiring?.dcBlackFeet || "").trim() &&
      String(wiring?.acFeet || "").trim() &&
      String(wiring?.earthingFeet || "").trim()
        ? 100
        : 0;

    const ip = technicalData?.installationPhotos || {};
    const installationPhotosFilled = [
      ip.panelSideWithCustomer,
      ip.panelFrontWithCustomer,
      ip.inverterInstallationPhoto,
    ].filter(Boolean).length;
    const installationPhotosProgress =
      installationPhotosFilled === 3 ? 100 : (installationPhotosFilled / 3) * 100;

    const totalTechnicalProgress =
      (panelsProgress +
        wiringInverterProgress +
        installationPhotosProgress +
        earthingProgress +
        wiringProgress) /
      5;

    setPhaseCompletion({
      civil: Math.round(civilProgress),
      technical: Math.round(totalTechnicalProgress),
    });
  }, [civilData, technicalData]);

  const handleCivilSave = useCallback(async ({ silent = false } = {}) => {
    setIsSavingCivil(true);
    try {
      if (customerId) {
        // Save civil work data without marking as complete
        await civilWork.upsertForCustomer(customerId, {
          photos: civilData?.photos || {},
        });
        // Move customer status to civil_inprogress if it's pending
        const customer = await customers.getById(customerId);
        if (customer?.status === "civil_pending") {
          await customers.update(customerId, { status: "civil_inprogress" });
        }
        if (!silent) {
          showToast("Civil work progress saved successfully!", "success");
        }
        setCivilLastSavedAt(new Date());
      }
    } catch (error) {
      console.error("Error saving civil work data:", error);
      if (silent) {
        showToast(
          error?.message || "Failed to auto-save civil progress.",
          "error",
        );
      } else {
        showToast("Failed to save progress. Please try again.", "error");
      }
    } finally {
      setIsSavingCivil(false);
    }
  }, [customerId, civilData?.photos, showToast]);

  const scheduleCivilAutoSave = useCallback(() => {
    if (!customerId || activePhase !== "civil") return;
    if (civilSaveTimeoutRef.current) clearTimeout(civilSaveTimeoutRef.current);
    civilSaveTimeoutRef.current = setTimeout(() => {
      civilSaveTimeoutRef.current = null;
      handleCivilSave({ silent: true }).catch(() => {});
    }, CIVIL_SAVE_DEBOUNCE_MS);
  }, [customerId, activePhase, handleCivilSave]);

  const handleCivilDataUpdate = useCallback(
    (nextData) => {
      setCivilData(nextData);
      scheduleCivilAutoSave();
    },
    [scheduleCivilAutoSave],
  );

  useEffect(() => {
    return () => {
      if (civilSaveTimeoutRef.current) clearTimeout(civilSaveTimeoutRef.current);
      if (technicalSaveTimeoutRef.current) {
        clearTimeout(technicalSaveTimeoutRef.current);
      }
    };
  }, []);

  const handleCivilComplete = async () => {
    if (phaseCompletion?.civil === 100) {
      setIsCompletingCivil(true);

      try {
        if (customerId) {
          // Save civil work data
          await civilWork.upsertForCustomer(customerId, {
            photos: civilData?.photos || {},
            submitted_by: user?.id || null,
            approval_status: "pending",
            approval_decision_by: null,
            approval_decision_at: null,
            disapproval_reason: null,
          });
          // Only move to civil_complete when still in civil phase statuses.
          const latestCustomer = await customers.getById(customerId);
          if (
            (STATUS_ORDER.civil_complete || 0) >
            (STATUS_ORDER[latestCustomer?.status] || 0)
          ) {
            await customers.update(customerId, { status: "civil_complete" });
          }

          // Update assignment status to completed
          try {
            const existingAssignment =
              await staffAssignments.getByCustomerAndUser(customerId, user.id);
            if (existingAssignment) {
              await staffAssignments.markCompleted(customerId, user.id);
            } else {
              // Create assignment and mark as completed
              await staffAssignments.create(customerId, user.id);
              await staffAssignments.markCompleted(customerId, user.id);
            }
          } catch (assignmentError) {
            // Log error but don't block completion
            console.error("Error updating assignment status:", assignmentError);
          }

          // Create notification for cluster manager
          try {
            await notificationsAPI.notifyClusterManagerOnWorkCompletion(
              customerId,
              "civil",
            );
          } catch (notifError) {
            // Log error but don't block completion
            console.error(
              "Error creating cluster manager notification:",
              notifError,
            );
          }

          // Show completion toast and navigate back to dashboard
          showToast("Civil work completed successfully!", "success");
          setTimeout(() => {
            navigate("/worker-dashboard");
          }, 1500);
        }
      } catch (error) {
        console.error("Error saving civil work data:", error);
        showToast("Failed to complete civil work. Please try again.", "error");
      } finally {
        setIsCompletingCivil(false);
        setIsVerifyingLocation(false);
      }
    }
  };

  const handleTechnicalSave = useCallback(
    async ({ silent = false } = {}) => {
      if (!customerId) return;

      const td = technicalDataRef.current;
      setIsSavingTechnical(true);
      try {
        await technicalWork.upsertForCustomer(customerId, {
          panels: ensureFourFixedPanels(td?.panels || {}),
          wiring_inverter: normalizeWiringInverter(td?.wiringInverter || {}),
          earthing: td?.earthing || {},
          installation_photos: td?.installationPhotos || {},
          wiring: td?.wiring || {},
        });
        const customer = await customers.getById(customerId);
        if (customer?.status === "technical_pending") {
          await customers.update(customerId, { status: "technical_inprogress" });
        }
        if (!silent) {
          showToast("Technical work progress saved successfully!", "success");
        }
        setTechnicalLastSavedAt(new Date());
      } catch (error) {
        console.error("Error saving technical work data:", error);
        if (silent) {
          showToast(
            error?.message || "Failed to auto-save technical progress.",
            "error",
          );
        } else {
          showToast("Failed to save progress. Please try again.", "error");
        }
      } finally {
        setIsSavingTechnical(false);
      }
    },
    [customerId, showToast],
  );

  const scheduleTechnicalAutoSave = useCallback(() => {
    if (!customerId || activePhase !== "technical") return;
    if (technicalSaveTimeoutRef.current) {
      clearTimeout(technicalSaveTimeoutRef.current);
    }
    technicalSaveTimeoutRef.current = setTimeout(() => {
      technicalSaveTimeoutRef.current = null;
      handleTechnicalSave({ silent: true }).catch(() => {});
    }, TECHNICAL_SAVE_DEBOUNCE_MS);
  }, [customerId, activePhase, handleTechnicalSave]);

  const handleTechnicalDataUpdate = useCallback(
    (nextData) => {
      technicalDataRef.current = nextData;
      setTechnicalData(nextData);
      scheduleTechnicalAutoSave();
    },
    [scheduleTechnicalAutoSave],
  );

  const handleTechnicalComplete = async () => {
    if (technicalSaveTimeoutRef.current) {
      clearTimeout(technicalSaveTimeoutRef.current);
      technicalSaveTimeoutRef.current = null;
    }

    setIsCompletingTechnical(true);

    try {
      if (customerId && user?.id) {
        const td = technicalDataRef.current;
        await technicalWork.upsertForCustomer(customerId, {
          panels: ensureFourFixedPanels(td?.panels || {}),
          wiring_inverter: normalizeWiringInverter(td?.wiringInverter || {}),
          earthing: td?.earthing || {},
          installation_photos: td?.installationPhotos || {},
          wiring: td?.wiring || {},
          installation_completed_at: new Date().toISOString(),
          submitted_by: user.id,
          approval_status: "pending",
          approval_decision_by: null,
          approval_decision_at: null,
          disapproval_reason: null,
        });
        const latestCustomer = await customers.getById(customerId);
        if (
          (STATUS_ORDER.technical_complete || 0) >
          (STATUS_ORDER[latestCustomer?.status] || 0)
        ) {
          await customers.update(customerId, { status: "technical_complete" });
        }
        try {
          const existingAssignment =
            await staffAssignments.getByCustomerAndUser(customerId, user.id);

          if (existingAssignment) {
            await staffAssignments.markCompleted(customerId, user.id);
          } else {
            await staffAssignments.create(customerId, user.id);
            await staffAssignments.markCompleted(customerId, user.id);
          }
        } catch (assignmentError) {
          console.error("Error updating staff assignment:", assignmentError);
        }

        try {
          await notificationsAPI.notifyClusterManagerOnWorkCompletion(
            customerId,
            "technical",
          );
        } catch (notifError) {
          console.error(
            "Error creating cluster manager notification:",
            notifError,
          );
        }

        setShowSuccessModal(true);
        localStorage.setItem("installationComplete", "true");
        localStorage.setItem("completionDate", new Date()?.toISOString());
      }
    } catch (error) {
      console.error("Error saving technical work data:", error);
      showToast(
        "Failed to complete technical work. Please try again.",
        "error",
      );
    } finally {
      setIsCompletingTechnical(false);
      setIsVerifyingLocation(false);
    }
  };

  const handleBackToDashboard = () => {
    navigate("/worker-dashboard");
  };

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedNavigation />
      <div className="content-area">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <div className="flex items-center justify-between mb-6 md:mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">
                Installation Workflow
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Complete all phases to finalize solar panel installation
              </p>
            </div>
            <Button
              variant="outline"
              onClick={handleBackToDashboard}
              iconName="ArrowLeft"
              iconPosition="left"
              className="hidden md:flex"
            >
              Back to Dashboard
            </Button>
          </div>

          <div className="space-y-6 md:space-y-8">
            <CustomerInfoCard customer={customerData} />

            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="p-4 md:p-6 lg:p-8">
                {user?.role === "worker" && activePhase === "civil" && (
                  <CivilWorkPhase
                    data={civilData}
                    onUpdate={handleCivilDataUpdate}
                    onComplete={handleCivilComplete}
                    customerId={customerId}
                    uploadPhoto={civilWork.uploadPhoto}
                    isSaving={isSavingCivil}
                    isCompleting={isCompletingCivil}
                    isVerifyingLocation={isVerifyingLocation}
                    overlayMeta={{
                      customerName: customerData?.name || "",
                      village: customerData?.village || "",
                      region:
                        customerData?.regions?.name ||
                        customerData?.regions?.region_name ||
                        "",
                    }}
                    capturedLocation={buildCapturedLocation(
                      customerData?.latitude,
                      customerData?.longitude,
                    )}
                  />
                )}

                {user?.role === "worker" && activePhase === "technical" && (
                  <>
                    <TechnicalWorkPhase
                      data={technicalData}
                      onUpdate={handleTechnicalDataUpdate}
                      onComplete={handleTechnicalComplete}
                      customerId={customerId}
                      uploadPhoto={technicalWork.uploadPhoto}
                      isSaving={isSavingTechnical}
                      isCompleting={isCompletingTechnical}
                      isVerifyingLocation={isVerifyingLocation}
                      currentStep={technicalStep}
                      onStepChange={setTechnicalStep}
                    />
                  </>
                )}

                <div className="mt-3 text-xs text-muted-foreground">
                  {activePhase === "civil" && isSavingCivil ? (
                    <span className="text-primary/80 animate-pulse">Saving...</span>
                  ) : activePhase === "civil" && civilLastSavedAt ? (
                    <span className="text-success">Data saved</span>
                  ) : activePhase === "technical" && isSavingTechnical ? (
                    <span className="text-primary/80 animate-pulse">Saving...</span>
                  ) : activePhase === "technical" && technicalLastSavedAt ? (
                    <span className="text-success">Data saved</span>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showSuccessModal && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[2000] flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-lg shadow-2xl max-w-md w-full p-6 md:p-8">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-success/10 flex items-center justify-center mb-4">
                <Icon name="CheckCircle2" size={40} className="text-success" />
              </div>
              <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-2">
                Installation Complete!
              </h2>
              <p className="text-sm md:text-base text-muted-foreground mb-6">
                All phases completed successfully and documentation is ready for
                review.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 w-full">
                <Button
                  variant="outline"
                  fullWidth
                  onClick={() => {
                    setShowSuccessModal(false);
                    navigate("/worker-dashboard");
                  }}
                >
                  Back to Dashboard
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InstallationWorkflow;
